/**
 * Translucent version of a column or board accent colour.
 *
 * Extracted from `components/KanbanColumn.tsx`, and hardened: the original
 * handled only 6-digit hex and returned `undefined` on anything else, so a
 * 3-digit hex or an `rgb()` string silently produced no tint at all. Returning
 * `undefined` for genuinely unparseable input is deliberate — callers treat it
 * as "no tint" rather than painting a wrong colour.
 */
export function tint(color: string | undefined, alpha: number): string | undefined {
  const value = color?.trim();
  if (!value) return undefined;

  const rgb = parseColor(value);
  if (!rgb) return undefined;

  const [r, g, b] = rgb;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function parseColor(value: string): [number, number, number] | null {
  if (value.startsWith('#')) {
    const hex = value.slice(1);

    // #abc → #aabbcc
    if (hex.length === 3) {
      const [r, g, b] = hex.split('').map((c) => parseInt(c + c, 16));
      return isValid(r, g, b) ? [r, g, b] : null;
    }

    if (hex.length === 6 || hex.length === 8) {
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      return isValid(r, g, b) ? [r, g, b] : null;
    }

    return null;
  }

  const match = value.match(/^rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)/i);
  if (match) {
    const [r, g, b] = match.slice(1, 4).map(Number);
    return isValid(r, g, b) ? [r, g, b] : null;
  }

  return null;
}

const isValid = (...parts: number[]) =>
  parts.every((n) => Number.isFinite(n) && n >= 0 && n <= 255);
